import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, TextField, InputAdornment, Grid, Paper, IconButton,
  CircularProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import default_theme from "assets/themes/themes";
import { fetchDataRequest } from "../../redux/api/apiSlice"; 
import { useParams } from "react-router-dom";

const SolutionsCatalogDialog = ({ open, onClose, onAttach, onDetach, attachedCount, existingAttachments }) => {  const dispatch = useDispatch();
  const [search, setSearch] = useState("");

  const { projectId } = useParams();

  const rawCatalogData = useSelector((state) => state.api?.datasets?.["Solutions Catalog"] || []);
  const loading = useSelector((state) => state.api?.loading || false);

  useEffect(() => {
    if (open && rawCatalogData.length === 0) {
      dispatch(fetchDataRequest({ 
        datasetName: "Solutions Catalog", 
        page: 1, 
        pageSize: 1000 
      }));
    }
  }, [open, dispatch, rawCatalogData.length]);

  // SAFELY Format the complex Cassandra structure
  const formattedSolutions = rawCatalogData.map(item => {
    let combined = item;
    if (typeof item.combinedData === 'string') {
      try { combined = JSON.parse(item.combinedData); } catch (e) { }
    } else if (item.combinedData) {
      combined = item.combinedData;
    }
    
    // Safely extract text (some database fields might be objects or missing)
    const getSafeText = (val) => {
      if (!val) return "";
      if (typeof val === 'string') return val;
      if (val.en) return val.en;
      if (val.english) return val.english;
      return String(val);
    };

    return {
      id: String(item.entryId || combined.id || item.id), // Ensure ID is a string
      title: getSafeText(combined.title_en || combined.title_english || combined.title) || "Untitled Solution",
      country: getSafeText(combined.location_country) || "Unknown Location",
      description: getSafeText(combined.story || combined.aims || combined.aim) || "No description available."
    };
  });

  // Apply search filter safely
  const safeSearch = (search || "").toLowerCase();
  const filteredSolutions = formattedSolutions.filter(sol => 
    (sol.title || "").toLowerCase().includes(safeSearch) || 
    (sol.country || "").toLowerCase().includes(safeSearch)
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { backgroundColor: "#F9F6ED" } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h5" sx={{ fontWeight: 500 }}>Solutions Catalog</Typography>
        <Typography variant="body2" sx={{ color: attachedCount >= 3 ? "error.main" : "text.secondary" }}>
          {attachedCount} / 3 Attached
        </Typography>
      </DialogTitle>
      
      <DialogContent dividers sx={{ minHeight: "400px" }}>
        <TextField
          fullWidth
          placeholder="Search by title or country..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ mb: 3, backgroundColor: "#fff", borderRadius: 1 }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
        />
        
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
            <CircularProgress sx={{ color: default_theme.palette.other.chatGreen }} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredSolutions.map((solution) => {
              // Ensure we compare strings so it never mismatches types
              const isAlreadyAttached = existingAttachments.some(a => String(a.id) === String(solution.id));
              
              return (
                <Grid item xs={12} sm={6} md={4} key={solution.id}>
                  <Paper 
                    onClick={() => {
                        const backPath = encodeURIComponent(`/workspaces/projects/${projectId}/step-3`);
                        const backLabel = encodeURIComponent("Back to Project");
                        window.open(`/solutions/rural-development/solutions-catalog/${solution.id}?backPath=${backPath}&backLabel=${backLabel}`, '_blank');
                      }}
                    sx={{ 
                      p: 3, height: "100%", display: "flex", flexDirection: "column", 
                      borderRadius: 3, position: "relative", cursor: "pointer",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": { transform: "translateY(-4px)", boxShadow: 4 }
                    }}>
                    
                    
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, pr: 3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {solution.title}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ color: "#667085", mb: 2, flexGrow: 1, display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {solution.description}
                    </Typography>
                    
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                      <Typography variant="caption" sx={{ border: "1px solid #ccc", borderRadius: 4, px: 1, py: 0.2 }}>
                        {solution.country}
                      </Typography>
                      
                      <Button 
                        variant={isAlreadyAttached ? "outlined" : "contained"}
                        size="small"
                        // Only disable if it is NOT attached AND we hit the 3-item limit
                        disabled={!isAlreadyAttached && attachedCount >= 3}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevents opening the card
                          if (isAlreadyAttached) {
                            onDetach(String(solution.id));
                          } else {
                            onAttach({ id: String(solution.id), title: solution.title });
                          }
                        }}
                        sx={{ 
                          textTransform: "none", 
                          backgroundColor: isAlreadyAttached ? "transparent" : default_theme.palette.other.chatGreen,
                          color: isAlreadyAttached ? "#d32f2f" : "#fff", // Red color for detach
                          borderColor: isAlreadyAttached ? "#d32f2f" : "transparent",
                          "&:hover": { 
                            backgroundColor: isAlreadyAttached ? "#fef2f2" : default_theme.palette.other.chatGreenHover,
                            borderColor: isAlreadyAttached ? "#d32f2f" : "transparent"
                          }
                        }}
                      >
                        {isAlreadyAttached ? "Detach" : "Attach To Challenge"}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
            
            {!loading && filteredSolutions.length === 0 && (
              <Box sx={{ width: "100%", textAlign: "center", mt: 4 }}>
                <Typography color="text.secondary">No solutions found matching "{search}".</Typography>
              </Box>
            )}
          </Grid>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button 
          variant="contained"
          onClick={onClose} 
          sx={{ 
            backgroundColor: "#23576B", 
            color: "#fff", 
            fontWeight: 600, 
            textTransform: "none", // Prevents the default all-caps look
            borderRadius: 2,       // Gives it nice rounded corners
            px: 4,                 // Adds horizontal padding to make it wider
            "&:hover": { 
              backgroundColor: "#1B4454" // Slightly darker blue on hover
            } 
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SolutionsCatalogDialog;